import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Tag } from 'lucide-react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AutoTagArticleInput, AutoTagArticleResponseDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { AutoSizeInput } from '@/Domain/components/Shared/AutosizeInput/AutosizeInput';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FormDescription, FormField, FormLabel } from '@/Domain/components/Shared/Form/Form';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import AddIcon from '@/Domain/components/icons/AddIcon';

export type TagObject = { value: string };

type Props = {
  form: UseFormReturn<any>; // Article form; we only access tagsObjects and the tags baseline
  articleId?: number | string;
};

const MAX_TAGS = 100;
const MAX_TAG_LENGTH = 50;
export const TagsField = ({ form, articleId }: Props) => {
  const { t } = useTranslation(['knowledge_base']);

  const autoTagMutation = useMutation<AutoTagArticleResponseDto, AxiosError, AutoTagArticleInput>({
    mutationKey: [QueryKey.knowledgeBase.articles.autoTag],
  });

  const { fields, append, remove } = useFieldArray({
    name: 'tagsObjects',
    control: form.control,
  });

  const handleAddField = () => append({ value: '' }, { focusIndex: fields.length });
  const handleRemoveField = (index: number) => remove(index);
  const handleAutoTag = () => {
    if (articleId == null) return;
    const id = Number(articleId);
    if (Number.isNaN(id)) return;
    // Send the form's current content/title/url so unsaved edits get tagged,
    // not whatever stale copy lives in the DB. Overrides are prompt-only.
    const values = form.getValues();
    autoTagMutation.mutate(
      {
        articleId: id,
        content: typeof values?.content === 'string' ? values.content : undefined,
        title: typeof values?.title === 'string' ? values.title : undefined,
        url: typeof values?.url === 'string' ? values.url : undefined,
      },
      {
        onSuccess: (data) => {
          // `tags` is useArticleSheet's tags-unchanged baseline, so it has to move with
          // `tagsObjects`: left stale, the next save resends these as a user edit and
          // Article.sheet's AUTO_TAGGED handler won't repair it once rows exist.
          const options = { shouldDirty: false, shouldTouch: false };
          form.setValue('tags', data.tags, options);
          form.setValue(
            'tagsObjects',
            data.tags.map((value) => ({ value })),
            options,
          );
        },
      },
    );
  };

  return (
    <div id="article_tags">
      <div className="flex items-center justify-between">
        <FormLabel>{t('knowledge_base:tags.title', { defaultValue: 'Article tags' })}</FormLabel>
        {articleId != null && fields.length === 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAutoTag}
            disabled={autoTagMutation.isPending}
            className="flex items-center gap-1.5 text-muted-foreground"
          >
            {autoTagMutation.isPending ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <Tag className="h-3.5 w-3.5" />
            )}
            {autoTagMutation.isPending
              ? t('knowledge_base:tags.auto_tagging', { defaultValue: 'Tagging…' })
              : t('knowledge_base:tags.auto_tag', { defaultValue: 'Auto-tag with AI' })}
          </Button>
        )}
      </div>
      <FormDescription className="my-4">
        {t('knowledge_base:tags.description', {
          defaultValue:
            'Use Tags to quickly filter Articles and help your AI Agent organize and respond more effectively.',
        })}
      </FormDescription>

      <div className="flex items-center flex-wrap gap-2 mt-2.5">
        {fields.map((arrayField, index) => (
          <FormField
            key={arrayField.id}
            name={`tagsObjects.${index}.value`}
            render={({ field }) => (
              <Badge onClose={() => handleRemoveField(index)} size="lg" variant="secondary">
                <AutoSizeInput
                  {...field}
                  inputProps={{ maxLength: MAX_TAG_LENGTH }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      const val = typeof field.value === 'string' ? field.value.trim() : '';
                      if (val.length === 0) {
                        event.preventDefault();
                        return handleRemoveField(index);
                      }
                      return handleAddField();
                    }
                  }}
                  onBlur={(e) => {
                    // Preserve RHF blur behaviour, then remove empty tags
                    field.onBlur?.();
                    const val = (e.target as HTMLInputElement).value?.trim() ?? '';
                    if (val.length === 0) {
                      handleRemoveField(index);
                    }
                  }}
                />
              </Badge>
            )}
          />
        ))}

        <Button
          type="button"
          variant="link"
          onClick={handleAddField}
          className="!pl-0"
          disabled={fields.length >= MAX_TAGS}
        >
          <AddIcon />
          {t('knowledge_base:tags.add', { defaultValue: 'Add Tag' })}
        </Button>
      </div>
    </div>
  );
};
